import React, {useState} from 'react';
import {Rate, Input, Button, Popconfirm, message, Select, Image, Tag} from 'antd';
import type {TableColumnsType} from 'antd';
import {DeleteOutlined, StarFilled, ShopOutlined} from '@ant-design/icons';
import {addKeyData, formatDate} from 'utils';
import type {SearchProps} from 'antd/es/input/Search';
import Api from 'configs';
import {useNavigate} from 'react-router-dom';

import {TableCustom} from 'components/element/TableCustom';
import {PaginationCustom} from 'components/element/PaginationCustom';
import {useGetShopReviews} from 'hooks/report';

export const ShopReviewsTab: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<any>({
    page: 1,
    pageSize: 10,
    search: '',
    rating: undefined,
  });
  const {data: getData, isLoading, refetch} = useGetShopReviews(filters);

  const getRatingTag = (rating: number) => {
    if (rating >= 4) return <Tag color="success">{rating} ★</Tag>;
    if (rating === 3) return <Tag color="warning">{rating} ★</Tag>;
    return <Tag color="error">{rating} ★</Tag>;
  };

  const columns: TableColumnsType = [
    {
      title: 'Mã đơn hàng',
      dataIndex: 'order',
      key: 'orderCode',
      align: 'center',
      width: 140,
      render: (order: any) => {
        if (!order?.code) return <span className="text-gray-400">—</span>;
        return (
          <span
            onClick={() => order?._id && navigate(`/orders/${order._id}`)}
            className="font-semibold text-blue-600 hover:underline cursor-pointer whitespace-nowrap">
            {order.code}
          </span>
        );
      },
    },
    {
      title: 'Cửa hàng (Quán)',
      dataIndex: 'shop',
      key: 'shop',
      align: 'left',
      width: 250,
      render: (shop: any) => {
        if (!shop?.name) {
          return <span className="text-gray-400 italic">Quán đã xóa hoặc không tồn tại</span>;
        }
        return (
          <div className="flex items-center gap-3">
            {shop.avatar ? (
              <Image
                src={shop.avatar}
                alt={shop.name}
                width={40}
                height={40}
                className="rounded-lg object-cover flex-shrink-0"
                fallback="/empty.jpg"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600 flex-shrink-0">
                <ShopOutlined />
              </div>
            )}
            <div className="flex flex-col min-w-0">
              <span className="font-semibold text-gray-800 text-sm truncate" title={shop.name}>
                {shop.name}
              </span>
              {shop.numberPhone && (
                <span className="text-xs text-gray-500">{shop.numberPhone}</span>
              )}
              {shop.address && (
                <span className="text-xs text-gray-400 truncate max-w-xs" title={shop.address}>
                  📍 {shop.address}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Khách hàng',
      dataIndex: 'client',
      key: 'client',
      align: 'center',
      width: 170,
      render: (client: any) => {
        return (
          <div className="flex flex-col items-center">
            <span className="font-medium text-gray-800 whitespace-nowrap">
              {client?.name || 'Khách hàng ẩn danh'}
            </span>
            {client?.numberPhone && (
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {client.numberPhone}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Đánh giá sao',
      dataIndex: 'rating',
      key: 'rating',
      align: 'center',
      width: 180,
      render: (rating: number) => {
        return (
          <div className="flex flex-col items-center gap-1">
            <Rate disabled value={rating} />
            {getRatingTag(rating)}
          </div>
        );
      },
    },
    {
      title: 'Nội dung nhận xét & Phản hồi',
      dataIndex: 'description',
      key: 'description',
      align: 'left',
      width: 320,
      render: (desc: string, record: any) => {
        return (
          <div className="flex flex-col gap-1.5">
            {desc ? (
              <div className="text-sm text-gray-800 bg-amber-50 px-3 py-1.5 rounded border border-amber-200">
                📝 {desc}
              </div>
            ) : (
              <span className="text-xs text-gray-400 italic">
                (Chỉ đánh giá số sao, không có nhận xét text)
              </span>
            )}
            {record?.shopReply && (
              <div className="text-xs text-blue-800 bg-blue-50 px-2.5 py-1 rounded border border-blue-200">
                💬 <b>Quán phản hồi:</b> {record.shopReply}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Ngày đánh giá',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center',
      width: 160,
      render: data => {
        return <div className="whitespace-nowrap">{formatDate(data)}</div>;
      },
    },
    {
      title: 'Hành động',
      key: 'action',
      align: 'center',
      width: 110,
      render: (_: any, record: any) => (
        <Popconfirm
          title="Xóa đánh giá"
          description="Bạn có chắc muốn xóa đánh giá này của khách hàng? Thao tác này sẽ gỡ cả số sao và bình luận khỏi quán."
          okText="Xóa"
          cancelText="Hủy"
          okButtonProps={{danger: true}}
          onConfirm={async () => {
            try {
              await Api.delete(`/reviews/${record?._id}`);
              message.success('Đã xóa đánh giá thành công');
              refetch();
            } catch {
              message.error('Lỗi khi xóa đánh giá');
            }
          }}>
          <Button danger size="small" icon={<DeleteOutlined />}>
            Xóa
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const onSearchValue: SearchProps['onSearch'] = (value: string) => {
    setFilters({...filters, search: value, page: 1});
  };

  const handleRatingFilter = (value: any) => {
    setFilters({...filters, rating: value, page: 1});
  };

  return (
    <div className="flex-col gap-4 mt-2">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div>
          <span className="text-gray-500 text-sm">
            Tổng số: <b>{getData?.total || 0}</b> đánh giá của khách hàng đối với cửa hàng
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Select
            placeholder="Lọc theo số sao"
            className="w-48"
            allowClear
            value={filters.rating}
            onChange={handleRatingFilter}
            options={[
              {label: '⭐ Tất cả số sao', value: undefined},
              {label: '⭐⭐⭐⭐⭐ 5 sao', value: 5},
              {label: '⭐⭐⭐⭐ 4 sao', value: 4},
              {label: '⭐⭐⭐ 3 sao', value: 3},
              {label: '⭐⭐ 2 sao', value: 2},
              {label: '⭐ 1 sao', value: 1},
            ]}
          />
          <Input.Search
            className="w-80"
            placeholder="Tìm theo quán, khách hàng, mã đơn..."
            onSearch={onSearchValue}
            enterButton
            allowClear
          />
        </div>
      </div>
      <div className="flex flex-col items-end gap-3">
        <TableCustom
          isLoading={isLoading}
          columns={columns}
          data={addKeyData(getData?.result)}
          scroll={{x: 1350, y: 590}}
        />
        <PaginationCustom
          totalPage={getData?.totalPage ?? 1}
          current={getData?.page ?? 1}
          pageSize={getData?.pageSize ?? 10}
          onChange={(page, pageSize) => {
            setFilters({...filters, page, pageSize});
          }}
          onShowSizeChange={(current, size) => {
            setFilters({...filters, page: current, pageSize: size});
          }}
        />
      </div>
    </div>
  );
};
